import React from "react";
import { sendErrorAlert } from "./errorAlertApi";

// Auto-reload once per session, then fall back to the manual button so a
// deterministic crash-on-load can't loop forever.
const AUTO_RELOADED_KEY = "__errAutoReloaded";
const AUTO_RELOAD_MS = 20000;

// Self-contained styles — the app's CSS/context may be exactly what broke, so
// the fallback depends on nothing outside this file.
const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    background: "#f5f6f8",
    zIndex: 999999,
    fontFamily: "Arial, Helvetica, sans-serif",
  },
  card: {
    maxWidth: "440px",
    width: "100%",
    background: "#ffffff",
    borderRadius: "14px",
    boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
    padding: "40px 32px",
    textAlign: "center",
    boxSizing: "border-box",
  },
  icon: { fontSize: "44px", lineHeight: 1, marginBottom: "16px" },
  heading: {
    margin: "0 0 10px",
    fontSize: "22px",
    color: "#1f2937",
    fontWeight: 700,
  },
  message: {
    margin: "0 0 24px",
    fontSize: "15px",
    color: "#6b7280",
    lineHeight: 1.6,
  },
  button: {
    display: "inline-block",
    border: "none",
    borderRadius: "8px",
    background: "#f82643",
    color: "#ffffff",
    fontSize: "15px",
    fontWeight: 600,
    padding: "12px 28px",
    cursor: "pointer",
  },
  note: { margin: "18px 0 0", fontSize: "12px", color: "#9ca3af" },
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, autoReloading: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Log for local debugging.
    // eslint-disable-next-line no-console
    console.error(
      "App crashed (ErrorBoundary):",
      error,
      info && info.componentStack,
    );

    // Notify the team (guarded, fire-and-forget).
    try {
      sendErrorAlert({ error, componentStack: info && info.componentStack });
    } catch (e) {}

    // Auto-reload once per session.
    let alreadyReloaded = true;
    try {
      alreadyReloaded = sessionStorage.getItem(AUTO_RELOADED_KEY) === "1";
    } catch (e) {}

    if (!alreadyReloaded) {
      try {
        sessionStorage.setItem(AUTO_RELOADED_KEY, "1");
      } catch (e) {}
      this.setState({ autoReloading: true });
      this.reloadTimer = setTimeout(() => {
        try {
          window.location.reload();
        } catch (e) {}
      }, AUTO_RELOAD_MS);
    }
  }

  componentWillUnmount() {
    if (this.reloadTimer) clearTimeout(this.reloadTimer);
  }

  handleReload = () => {
    try {
      window.location.reload();
    } catch (e) {}
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div style={styles.overlay} role="alert" aria-live="assertive">
        <div style={styles.card}>
          <div style={styles.icon}>🛠️</div>
          <h1 style={styles.heading}>We&apos;ll be right back</h1>
          <p style={styles.message}>
            The site is temporarily down. Please wait a few minutes and try
            again — our team has been notified and is looking into it.
          </p>
          <button
            type="button"
            style={styles.button}
            onClick={this.handleReload}
          >
            Reload now
          </button>
          {this.state.autoReloading && (
            <p style={styles.note}>
              This page will refresh automatically in a few seconds…
            </p>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
